import React, { useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import "./Insights.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import insightsData from "../../StaticData/InsightsData";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";

const sortedInsightsData = [...insightsData].sort(
  (a, b) => new Date(b.date) - new Date(a.date)
);

const Insights = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = sortedInsightsData.slice(indexOfFirstPost, indexOfLastPost);

  const totalPages = Math.ceil(sortedInsightsData.length / postsPerPage);
  const maxVisiblePages = 5;

  const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  const adjustedStartPage = Math.max(1, endPage - maxVisiblePages + 1);
  const pageNumbers = Array.from(
    { length: endPage - adjustedStartPage + 1 },
    (_, i) => adjustedStartPage + i
  );

  const paginate = (pageNumber) => {
    const safePage = parseInt(pageNumber, 10);
    if (!isNaN(safePage) && safePage >= 1 && safePage <= totalPages) {
      setCurrentPage(safePage);
      window.scrollTo(0, 600);
    }
  };

  const handleNavigation = (e, url) => {
    e.preventDefault();
    // Block dangerous schemes and external URLs
    if (
      !url ||
      /^(javascript|data|vbscript|file):/i.test(url.trim()) ||
      /^\/\//.test(url.trim()) ||
      /^https?:\/\//i.test(url.trim())
    ) {
      console.warn("Blocked unsafe URL:", url);
      return;
    }
    // Re-encode query param names and values to prevent HPP injection
    const [pathPart, queryPart] = url.split("?");
    if (!queryPart) {
      navigate(pathPart);
      return;
    }
    const sanitizedQuery = queryPart
      .split("&")
      .map((param) => {
        const eqIndex = param.indexOf("=");
        if (eqIndex === -1) return encodeURIComponent(decodeURIComponent(param));
        const name = param.substring(0, eqIndex);
        const value = param.substring(eqIndex + 1);
        return `${encodeURIComponent(decodeURIComponent(name))}=${encodeURIComponent(decodeURIComponent(value))}`;
      })
      .join("&");
    navigate(`${pathPart}?${sanitizedQuery}`);
  };

  return (
    <div className="insights-page">
      <Helmet>
        <title>
          Latest Articles about Cloud Computing Technology and Upcoming Events
        </title>
        <meta
          name="description"
          content="Our Technology blog provides latest articles/news about cloud computing, devops, big data, artificial intelligence, machine learning, cloud security and general tech news and events."
        />
      </Helmet>

      <motion.div
        className="Productright"
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Container fluid className="insights-banner-container p-0">
          <section className="insights-banner-section"></section>
        </Container>
      </motion.div>

      <div className="space"></div>

      {/* Posts Section */}
      <Container>
        <Row xs={1} md={3} className="g-4">
          {currentPosts.map((insight) => (
            <Col key={insight.id}>
              <Card className="h-100">
                <p className="date">{insight.date}</p>
                <Link
                  to={insight.buttonLink}
                  onClick={(e) => handleNavigation(e, insight.buttonLink)}
                >
                  <Card.Img
                    variant="top"
                    src={`${insight.imageName}`}
                    alt={`Insight ${insight.id}`}
                  />
                </Link>
                <Card.Body className="insightsbody">
                  <div className="tags mb-2">
                    {insight.tags.map((tag, index) => (
                      <span key={tag}>
                        <Link
                          to={`/tag/${encodeURIComponent(tag)}`}
                          className="tag-link boxtags"
                          onClick={(e) => handleNavigation(e, `/tag/${encodeURIComponent(tag)}`)}
                        >
                          {tag}
                        </Link>
                        {index < insight.tags.length - 1 && <span> | </span>}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={insight.buttonLink}
                    onClick={(e) => handleNavigation(e, insight.buttonLink)}
                  >
                    <Card.Title>{insight.title}</Card.Title>
                  </Link>
                </Card.Body>
                <Link
                  to={insight.buttonLink}
                  onClick={(e) => handleNavigation(e, insight.buttonLink)}
                  className="read-more"
                >
                  {insight.buttonLabel}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    style={{ margin: "5px", float: "right" }}
                  />
                </Link>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
      <div className="space"></div>

      {/* Pagination */}
      <Container>
        <Row>
          <Col className="d-flex justify-content-center">
            <nav>
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => paginate(1)} disabled={currentPage === 1}>
                    First
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                    Previous
                  </button>
                </li>
                {pageNumbers.map((page) => (
                  <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                    <button onClick={() => paginate(page)} className="page-link">
                      {page}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                    Next
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => paginate(totalPages)} disabled={currentPage === totalPages}>
                    Last
                  </button>
                </li>
              </ul>
            </nav>
          </Col>
        </Row>
      </Container>
      <div className="space"></div>
    </div>
  );
};

export default Insights;
